import { useCallback, useMemo, useState } from 'react';
import { Text, Button, Alert, AlertTitle, AlertIcon, AlertDescription, Box } from '@chakra-ui/react';

import { Modal } from 'shared-components';
import { AccommodationArrangedStatusEnum, AccommodationTypeStatusEnum } from 'types';

import { IAccommodationStatus } from '../../../../../../types';

import { AdditionalQuestions } from './helpers/additional-questions';
import { ConfirmationBlock } from './helpers/confirmation-block';
import { HaveYouArrangedAccommodation } from './helpers/have-you-arranged-accommodation';
import { IntroWrapper } from './helpers/intro-wrapper';

interface IProps {
  onModalClose: () => void;
  saveAccommodationStatus: (data: IAccommodationStatus) => void;
  accommodationStatus: IAccommodationStatus;
  universityAccommodationEnabled: boolean;
}

export const AccommodationStatusModal = (props: IProps) => {
  const { onModalClose, saveAccommodationStatus, universityAccommodationEnabled } = props;

  const accommodationArrangedStatus = props.accommodationStatus.arrangedStatus || AccommodationArrangedStatusEnum.NONE;
  const accommodationTypeStatus = props.accommodationStatus.typeStatus || AccommodationTypeStatusEnum.NONE;

  const [arrangedStatus, setArrangedStatus] = useState<AccommodationArrangedStatusEnum>(accommodationArrangedStatus);
  const [typeStatus, setTypeStatus] = useState<AccommodationTypeStatusEnum>(accommodationTypeStatus);
  const [showWarning, setShowWarning] = useState(false);

  const showConfirmationBlock =
    accommodationArrangedStatus !== AccommodationArrangedStatusEnum.NONE ||
    accommodationTypeStatus !== AccommodationTypeStatusEnum.NONE;

  const [isQuestionAllowed, setIsQuestionAllowed] = useState<boolean | undefined>(
    !showConfirmationBlock ? true : undefined
  );

  const [additionalQuestionsComplete, setAdditionalQuestionsComplete] = useState<boolean>(false);

  const saveAccommodationStatusHandler = useCallback(() => {
    saveAccommodationStatus({
      arrangedStatus: arrangedStatus,
      typeStatus: typeStatus
    });

    onModalClose();
  }, [saveAccommodationStatus, arrangedStatus, typeStatus, onModalClose]);

  const status = useMemo(
    () => ({
      arrangedStatus: accommodationArrangedStatus,
      typeStatus: accommodationTypeStatus
    }),
    [accommodationArrangedStatus, accommodationTypeStatus]
  );

  const closeEnabled = isQuestionAllowed === false;
  const submitEnabled = additionalQuestionsComplete && !closeEnabled;
  return (
    <Modal
      id={'AccommodationStatusModal'}
      show={true}
      title={'Update Your Accommodation Status'}
      onClose={onModalClose}
      footer={
        <Button
          id="enrolment-section-accommodation-status-save-button"
          isDisabled={!(submitEnabled || closeEnabled)}
          onClick={() => {
            if (submitEnabled) {
              saveAccommodationStatusHandler();
            } else {
              onModalClose();
            }
          }}
        >
          {closeEnabled ? 'Close' : 'Continue'}
        </Button>
      }
    >
      <div data-selector="enrolment-section-accommodation-status-modal">
        <IntroWrapper status={status} />

        {showConfirmationBlock && (
          <>
            <ConfirmationBlock setConfirmation={setIsQuestionAllowed} />

            {isQuestionAllowed === false && (
              <Text>If nothing has changed with your accommodation status you can close this window.</Text>
            )}
          </>
        )}

        {isQuestionAllowed && (
          <>
            <HaveYouArrangedAccommodation
              accommodationArrangedStatus={accommodationArrangedStatus}
              selectedAccommodationArrangedStatus={{
                status: arrangedStatus,
                setStatus: setArrangedStatus
              }}
            />

            <AdditionalQuestions
              status={status}
              universityAccommodationEnabled={universityAccommodationEnabled}
              selectedAccommodationArrangedStatus={{
                status: arrangedStatus
              }}
              selectedAccommodationTypeStatus={{
                status: typeStatus,
                setStatus: setTypeStatus
              }}
              setComplete={setAdditionalQuestionsComplete}
              showWarning={showWarning}
              setShowWarning={setShowWarning}
            />
          </>
        )}

        {(additionalQuestionsComplete || showWarning) && isQuestionAllowed && (
          <Alert
            status={showWarning ? 'warning' : 'info'}
            data-selector={'enrolment-section-accommodation-status-info-warning'}
          >
            <AlertIcon />

            <Box>
              <AlertTitle>
                {showWarning ? 'This accommodation is not available' : 'What if my accommodation plans change?'}
              </AlertTitle>

              <AlertDescription>
                {showWarning ? (
                  <>
                    <Text>University accommodation is not available. Please make another selection.</Text>

                    <Text mb={0}>
                      If you are unsure, select that you have not booked accommodation then select "I don't know yet"
                    </Text>
                  </>
                ) : (
                  <>
                    If your plans change, simply return to this page and update your status to make us aware of your new
                    situation.
                  </>
                )}
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </div>
    </Modal>
  );
};
